import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// ---------------------------------------------------------------------------
// Base route definitions — the single source of truth.
// Each route gets two versions:
//   1. Slug-prefixed  /:accountSlug/...  — used on shared domains (booking.gopgms.com)
//   2. Flat           /...               — used on whitelabel domains
// ---------------------------------------------------------------------------
const pageRoutes = [
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/pages/TermsPage.vue')
    },
    {
      path: '/cookies-policy',
      name: 'cookies-policy',
      component: () => import('@/pages/CookiesPolicyPage.vue')
    },
    {
      path: '/user-agreement',
      name: 'user-agreement',
      component: () => import('@/pages/UserAgreementPage.vue')
    },
    {
      path: '/refund-cancellation-policy',
      name: 'refund-cancellation-policy',
      component: () => import('@/pages/RefundCancellationPolicyPage.vue')
    },
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/HomePage.vue')
  },
  {
    path: '/properties',
    name: 'property-list',
    component: () => import('@/pages/PropertyListPage.vue')
  },
  {
    path: '/property/:slug/:propertyId?',
    name: 'property-details',
    component: () => import('@/pages/PropertyDetailsPage.vue')
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/pages/AboutPage.vue')
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/pages/ContactPage.vue')
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/pages/FAQPage.vue')
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/pages/PrivacyPolicyPage.vue')
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/pages/CheckoutPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-bookings',
    name: 'my-bookings',
    component: () => import('@/pages/MyBookingsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-inquiries',
    name: 'my-inquiries',
    component: () => import('@/pages/MyInquiriesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-complaints',
    name: 'my-complaints',
    component: () => import('@/pages/MyComplaintsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/complaint/:complaintId',
    name: 'complaint-details',
    component: () => import('@/pages/ComplaintDetailsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-profile',
    name: 'my-profile',
    component: () => import('@/pages/MyProfilePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/booking/:bookingId',
    name: 'booking-details',
    component: () => import('@/pages/BookingDetailsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/payment-status',
    name: 'payment-status',
    component: () => import('@/pages/PaymentStatusPage.vue')
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/pages/AuthPage.vue')
  }
]

// Generate slug-prefixed duplicates —  /:accountSlug  +  /:accountSlug/properties  etc.
const slugRoutes = pageRoutes.map((r) => ({
  ...r,
  path: r.path === '/'
    ? '/:accountSlug'
    : `/:accountSlug${r.path}`,
  name: `slug-${r.name}`,
  meta: { ...r.meta }
}))

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Slug-prefixed routes first (higher priority on shared domain)
    ...slugRoutes,
    // Flat routes (whitelabel)
    ...pageRoutes,
    // Marketplace landing — shared domain, no account slug
    {
      path: '/explore',
      name: 'explore',
      component: () => import('@/pages/MarketplaceLandingPage.vue'),
      meta: { fullscreen: true }
    },
    // Catch-all must be last
    {
      path: '/:pathMatch(.*)*',
      name: 'error',
      component: () => import('@/pages/ErrorPage.vue')
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    // Don't scroll to top when only query params change on the same route
    // (e.g. filter changes on property-list)
    if (to.name === from.name && to.path === from.path) {
      return false
    }

    return { top: 0 }
  }
})

router.beforeEach((to, from, next) => {
  if (to.meta.requiresAuth) {
    const authStore = useAuthStore()
    authStore.hydrateSession()

    if (!authStore.isAuthenticated) {
      return next({ name: 'home' })
    }
  }
  next()
})

export default router
